/**
 * Every rule the register holds, then every rule it does not.
 *
 * The rule editor is a route rather than a boolean, so the modal renders over this list with the
 * list still mounted behind it, and Cancel, Escape and the browser back button all close it.
 */

import { Outlet } from 'react-router';
import { Link } from 'react-router';
import { SectionCard } from '~/ui/Surface';
import { Button } from '~/ui/Button';
import { EmptyState } from '~/ui/Feedback';
import { RuleCard, AbsentRuleCard } from './RuleCard';
import { PendingChanges } from './PendingChanges';
import { useMatterView } from './useMatterView';
import { vocabularyOf } from './describe';
import { routes } from '~/lib/routes';
import { lowerLabel, plural } from '~/lib/format';
import styles from './Matter.module.css';

export function RulesTab() {
  const { panel, meta, code, matter, dates, life } = useMatterView();
  const vocab = vocabularyOf(meta.data);

  if (!panel.data) return null;
  const data = panel.data;

  // The first answer each rule produced, from whichever read is on screen. The server ordered
  // them; taking the first is reading that order, not deciding it.
  const computed = new Map<string, { date: string | null; reason: string | null }>();
  for (const deadline of life?.deadlines ?? []) {
    if (!computed.has(deadline.ruleId)) {
      computed.set(deadline.ruleId, { date: deadline.date, reason: deadline.reason });
    }
  }
  if (life?.spine) {
    computed.set(life.spine.ruleId, { date: life.spine.expiryOn, reason: null });
  }

  return (
    <>
      <PendingChanges code={code} matter={matter} />

      <SectionCard
        title={`${plural(data.rules.length, 'rule')} held`}
        note="A rule is a chain of dated statutory versions, and exactly one version governs any given filing date. Open a rule to edit its chain."
        maxWidth={1034}
        actions={
          <div style={{ marginTop: 10 }}>
            <Link to={routes.newRule(code, matter, dates)}>
              <Button>Add a rule</Button>
            </Link>
          </div>
        }
      >
        {data.rules.length === 0 ? (
          <EmptyState variant="bare">
            <p>
              This register holds no {lowerLabel(data.label)} rule yet. Adopt one from the list
              below, add a rule the catalogue has never heard of, or queue a research run.
            </p>
          </EmptyState>
        ) : (
          <div className={styles.ruleList}>
            {data.rules.map((rule) => (
              <RuleCard
                key={rule.id}
                code={code}
                matter={matter}
                rule={rule}
                vocab={vocab}
                dates={dates}
                computed={computed.get(rule.id)}
              />
            ))}
          </div>
        )}
      </SectionCard>

      {data.absentRules.length > 0 ? (
        <SectionCard
          title="Rules this register has never adopted"
          note="Naming what a register does not have is the only way the list reads as complete rather than as whatever happened to be found."
          maxWidth={1034}
        >
          <div className={styles.ruleList}>
            {data.absentRules.map((rule) => (
              <AbsentRuleCard
                key={rule.lane}
                code={code}
                matter={matter}
                rule={rule}
                dates={dates}
              />
            ))}
          </div>
        </SectionCard>
      ) : null}

      {/* The editor route renders here, over the list. */}
      <Outlet />
    </>
  );
}
